import React, { Component } from "react";
import styled from "styled-components";
import { DialogContent } from "@material-ui/core";
import { create, logout, type FrontifyAsset } from "@frontify/frontify-finder";

import getLogger, { LogGroup } from "js/core/logger";
import Spinner from "../Spinner";
import BeautifulDialog from "./BeautifulDialog";
import { ShowErrorDialog } from "./BaseDialog";
import { Button } from "../../../Components/Button";

const logger = getLogger(LogGroup.ASSETS);

const DialogContentWrapper = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 0;
    }
    #frontify-finder-container {
        height: 80vh;
    }
`;

interface Props {
    callback: (assets: FrontifyAsset[]) => void;
    closeDialog: () => void;
    extensions?: string[];
}

interface State {
    isPickerReady: boolean,
    showLogoutButton: boolean
}

class FrontifyFilePickerDialog extends Component<Props, State> {
    state = {
        isPickerReady: false,
        showLogoutButton: false
    };

    async componentDidMount() {
        try {
            const finder = await create({
                clientId: "beautiful-ai",
                options: {
                    allowMultiSelect: false,
                    autoClose: true,
                    permanentDownloadUrls: true,
                }
            });

            finder.onAssetsChosen((assets: FrontifyAsset[]) => {
                this.props.callback(assets);
                this.props.closeDialog();
            });

            finder.onCancel(() => {
                this.props.closeDialog();
            });

            finder.mount(document.getElementById("frontify-finder-container"));

            this.setState({ isPickerReady: true });

            // delay logout button overlay to wait for finder widget finish mounting
            setTimeout(() => {
                this.setState({ showLogoutButton: true });
            }, 1000);
        } catch (err) {
            // NOTE - frontify authenticator doesn't throw the correct error message (ERR_AUTH_DOMAIN_POPUP_CLOSED)
            // when the popup is closed or cancelled. It throws a generic error message about reading the token.

            if (err.message.includes("TypeError: Cannot read properties of undefined (reading 'bearerToken')")) {
                logger.warn(err, "User closed the Frontify authenticator popup, closing the picker dialog silently.");
                this.props.closeDialog();
                return;
            }

            logger.error(err, "Error while initializing Frontify file picker dialog.");
            this.props.closeDialog();
            ShowErrorDialog({ title: "Error", message: "Error while initializing Frontify file picker dialog." });
        }
    }

    handleLogout = async () => {
        await logout({ clientId: "beautiful-ai" });

        this.props.closeDialog();
    }

    render() {
        const { isPickerReady, showLogoutButton } = this.state;

        return (
            <BeautifulDialog
                style={{ opacity: isPickerReady ? 1 : 0 }}
                maxWidth="lg" closeButton closeDialog={this.props.closeDialog}
            >
                <DialogContentWrapper>
                    <div id="frontify-finder-container" />

                    {!isPickerReady && <Spinner />}
                    {isPickerReady && showLogoutButton && (
                        <Button warning onClick={() => this.handleLogout()}
                            style={{ position: "absolute", width: "200px", top: "calc(100% - 50px)", right: "calc(100% - 225px)", zIndex: 1000 }}>Logout</Button>
                    )}
                </DialogContentWrapper>
            </BeautifulDialog>
        );
    }
}

export default FrontifyFilePickerDialog;
